import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Text } from '@Sage/hoi-poi-ui';
import { normalizeDecimalValue } from 'utils/decimal';

import './styles.scss';

/** contentEditable-only noise; keeps commas/digits (locale decimal comma untouched). */
function stripContentEditableArtifacts(s) {
    return String(s ?? '').replace(/[\r\n\u200b\ufeff]/g, '');
}

function parsePositiveStep(value) {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
}

function insertAtSelection(currentValue, insertedValue, selectionStart, selectionEnd) {
    return currentValue.slice(0, selectionStart) + insertedValue + currentValue.slice(selectionEnd);
}

function getSelectionOffsets(element) {
    const selection = window.getSelection?.();
    if (!selection || !element || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const contained = (n) => n && (n === element || element.contains(n));
    if (!contained(range.startContainer) || !contained(range.endContainer)) {
        return null;
    }
    // Offsets are only meaningful for text inside the editable; never use child indices
    // on the element itself, or BR placeholder nodes, as string positions into innerText.
    const badAnchor = (n) => n === element || (n.nodeType === 1 && n.nodeName === 'BR');
    if (badAnchor(range.startContainer) || badAnchor(range.endContainer)) {
        return null;
    }

    return { start: range.startOffset, end: range.endOffset };
}

/** When selection cannot be mapped to stripped text, empty → insert at start; else append. */
function insertionIndices(selection, strippedValue) {
    const len = strippedValue.length;
    if (selection != null) {
        return [selection.start, selection.end];
    }
    return len === 0 ? [0, 0] : [len, len];
}

function isValidDecimalDraft(value, allowDecimals, limitDecimals) {
    if (!allowDecimals) {
        return /^\d+$/.test(stripContentEditableArtifacts(String(value)));
    }

    const normalized = stripContentEditableArtifacts(String(value)).replace(',', '.');
    if (normalized === '') return true;
    // Allow trailing `.` while typing (e.g. 0.5); blur clamps via parseValue.
    if (!/^-?\d*(?:\.\d*)?$/.test(normalized)) return false;
    if (limitDecimals === undefined || limitDecimals === null) return true;

    const decimalPart = normalized.split('.')[1] || '';
    return decimalPart.length <= limitDecimals;
}

function buildNextStrippedValue(element, insertedText) {
    const currentValue = stripContentEditableArtifacts(element?.innerText ?? '');
    const selection = getSelectionOffsets(element);
    const [start, end] = insertionIndices(selection, currentValue);
    const nextValue = insertAtSelection(currentValue, insertedText, start, end);
    return { currentValue, nextValue, selection };
}

function collapseCaretToEnd(element) {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const sel = window.getSelection();
    if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
    }
}

function QuantityItem({
    className,
    onChange,
    quantity,
    max,
    min = 0,
    allowDecimals = false,
    limitDecimals,
    step,
    isInvalid = false,
}) {
    const rootClassName = classNames('fm-quantity-input', className, {
        'fm-quantity-input--invalid': isInvalid,
    });
    const effectiveStep = parsePositiveStep(step) ?? 1;
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);

    const clampValueByDecimals = useCallback(
        (value) => {
            if (!allowDecimals) return value;

            const parsed = normalizeDecimalValue(value);
            if (parsed == null) return null;

            if (limitDecimals === undefined || limitDecimals === null) {
                return parsed;
            }

            if (limitDecimals <= 0) {
                return Math.round(parsed);
            }

            return Number(parsed.toFixed(limitDecimals));
        },
        [allowDecimals, limitDecimals],
    );

    const getDisplayValue = useCallback(
        (value) => {
            if (value === null || value === undefined || value === '') return String(min);

            if (!allowDecimals) return String(value);

            const parsed = normalizeDecimalValue(value);
            if (parsed == null) return String(min);

            if (limitDecimals === undefined || limitDecimals === null) {
                return String(parsed);
            }

            if (limitDecimals <= 0) {
                return String(Math.round(parsed));
            }

            return parsed.toFixed(limitDecimals);
        },
        [allowDecimals, limitDecimals, min],
    );

    const parseValue = useCallback(
        (value) => {
            if (allowDecimals) {
                return clampValueByDecimals(value);
            }

            const parsed = parseInt(value, 10);
            return isNaN(parsed) ? null : parsed;
        },
        [allowDecimals, clampValueByDecimals],
    );

    const clampValue = useCallback(
        (value) => {
            if (value == null) return min;
            if (typeof max === 'number' && value > max) return max;
            if (value < min) return min;
            return value;
        },
        [max, min],
    );

    useEffect(() => {
        if (!isFocused && inputRef.current) {
            inputRef.current.innerText = getDisplayValue(quantity);
        }
    }, [getDisplayValue, isFocused, quantity]);

    const handleKeyDown = useCallback(
        (e) => {
            const allowedKeys = [
                48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 96, 97, 98, 99, 100, 101, 102, 103, 104,
                105, 8, 37, 39, 9, 46,
            ];

            if (allowDecimals) {
                allowedKeys.push(188, 190, 110);
            }

            if (!allowedKeys.includes(e.keyCode)) {
                e.preventDefault();
                return;
            }

            if (!allowDecimals) return;

            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.keyCode === 8 || e.keyCode === 46 || e.keyCode === 37 || e.keyCode === 39) {
                return;
            }

            const el = e.currentTarget;
            const { currentValue, nextValue, selection } = buildNextStrippedValue(el, e.key);

            if (!isValidDecimalDraft(nextValue, allowDecimals, limitDecimals)) {
                e.preventDefault();
                return;
            }

            // Native insert can land in the wrong place when the DOM still has CE junk (e.g. \n).
            if (selection == null && currentValue === '' && e.key.length === 1) {
                e.preventDefault();
                el.innerText = nextValue;
                collapseCaretToEnd(el);
            }
        },
        [allowDecimals, limitDecimals],
    );

    const handlePaste = useCallback(
        (e) => {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text');
            const el = e.currentTarget;
            const { nextValue } = buildNextStrippedValue(el, text);
            if (isValidDecimalDraft(nextValue, allowDecimals, limitDecimals)) {
                el.innerText = nextValue;
            }
        },
        [allowDecimals, limitDecimals],
    );

    const handleFocus = useCallback(() => {
        setIsFocused(true);
        if (inputRef.current) {
            inputRef.current.innerText = getDisplayValue(quantity);
        }
    }, [getDisplayValue, quantity]);

    const onChangeSpan = useCallback(
        (e) => {
            const raw = e.currentTarget?.innerText ?? '';
            const nextValue = clampValue(parseValue(stripContentEditableArtifacts(raw)));
            if (e.currentTarget) {
                e.currentTarget.innerText = String(nextValue);
            }
            setIsFocused(false);
            onChange(nextValue);
        },
        [clampValue, onChange, parseValue],
    );

    // Round +/- results so we don't store FP noise; also respect fractional steps when
    // the parent only passes limitDecimals: 0 (e.g. schema) but step is 0.01.
    const roundToStep = useCallback(
        (value) => {
            if (!Number.isFinite(value)) return value;
            const fromProps =
                typeof limitDecimals === 'number' && limitDecimals > 0 ? limitDecimals : 0;
            const fromStep =
                effectiveStep % 1 === 0
                    ? 0
                    : (() => {
                          const t = String(effectiveStep);
                          if (t.includes('e-') || t.includes('E-')) return 0;
                          const p = t.split('.')[1];
                          return p ? Math.min(12, p.length) : 0;
                      })();
            const decimals = Math.max(fromProps, fromStep);
            if (decimals <= 0) return value;
            return Number(value.toFixed(decimals));
        },
        [effectiveStep, limitDecimals],
    );

    const addQuantity = useCallback(
        () => onChange(clampValue(roundToStep((quantity || 0) + effectiveStep))),
        [clampValue, effectiveStep, onChange, quantity, roundToStep],
    );

    const subQuantity = useCallback(
        () => onChange(clampValue(roundToStep((quantity || 0) - effectiveStep))),
        [clampValue, effectiveStep, onChange, quantity, roundToStep],
    );

    return (
        <div className={rootClassName}>
            <Text
                className={classNames('fm-quantity-input__left-action', {
                    disabled: quantity === min,
                })}
                onClick={subQuantity}
            >
                -
            </Text>
            <span
                ref={inputRef}
                className="fm-quantity-input__input"
                onBlur={onChangeSpan}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                contentEditable
                suppressContentEditableWarning
            >
                {getDisplayValue(quantity)}
            </span>
            <Text
                className={classNames('fm-quantity-input__right-action', {
                    disabled: max && quantity === max,
                })}
                onClick={addQuantity}
            >
                +
            </Text>
        </div>
    );
}

QuantityItem.propTypes = {
    allowDecimals: PropTypes.bool,
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    quantity: PropTypes.number.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
    step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isInvalid: PropTypes.bool,
};

export default memo(QuantityItem);
